import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import userService from '../services/userService'
import activityService from '../services/activityService'
import type { UserWithRole, PendingInvitation as APIPendingInvitation, APIUserResponse, InviteUserData } from '../types/user'
import type { ActivityLogEntry } from '../services/activityService'

export interface User {
  id: string
  firstName: string
  lastName: string
  jobTitle: string
  department: string
  departmentId?: string  // NEW: Store department_id from API
  userType: string
  subAdmin: string
  subAdminId?: string  // NEW: Store sub_admin_id from API
  linkedInProfile: string
  status: string
  email?: string
  creatorType?: string
  avatar?: string
  // Analytics metrics
  posts?: number
  reactions?: number
  comments?: number
  reshares?: number
  impressions?: number
  // Team/Editor fields
  departments?: string[]
  assignedUsers?: number
  // Contributor/Profile fields
  assignedEvents?: number
  pendingPosts?: number
  // Display name (computed from firstName + lastName)
  name?: string
  // Role (alias for jobTitle)
  role?: string
}

export interface PendingInvitation {
  id: string
  firstName: string
  lastName: string
  email: string
  status: string
  statusIcon: string
}

export interface Activity {
  id: string
  userId: string
  userName: string
  activityType: string
  activityName: string
  timestamp: string
}

export interface ContentItem {
  id: string
  userId: string
  postTitle: string
  lastModified: string
  status: 'saved' | 'drafts' | 'pending' | 'awaiting-approve' | 'published' | 'scheduled'
  statusDate?: string
}

export const useUsersStore = defineStore('users', () => {
  // Loading and error state
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Organization info
  const organization = ref({
    id: '',
    name: '',
    industry: '',
    size: '',
    linkedinPage: ''
  })

  // Centralized users data - starts empty, populated from API
  const users = ref<User[]>([])

  // Legacy: activeContributors (for backward compatibility)
  const activeContributors = computed(() => users.value.filter(u => u.creatorType === 'Creator' && u.status === 'Connected'))

  // Pending Invitations - starts empty, populated from API
  const pendingInvitations = ref<PendingInvitation[]>([])

  // Activities log - starts empty, populated from API
  const activities = ref<Activity[]>([])

  // Content items - starts empty, populated from API
  const contentItems = ref<ContentItem[]>([])

  // Computed properties for different views
  const totalActiveContributors = computed(() => activeContributors.value.length)
  const totalPendingInvitations = computed(() => pendingInvitations.value.length)
  const totalUsers = computed(() => users.value.length)

  // Get user by ID
  const getUserById = (userId: string): User | undefined => {
    return users.value.find(u => u.id === userId)
  }

  // Get user display name
  const getUserDisplayName = (user: User): string => {
    if (user.name) {
      return user.name
    }
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'
  }

  // Get user role (jobTitle)
  const getUserRole = (user: User): string => {
    return user.role || user.jobTitle || 'Unknown Role'
  }

  // Analytics users (with metrics)
  const analyticsUsers = computed(() => {
    return users.value
      .filter(u => u.posts !== undefined && u.posts > 0)
      .map(u => ({
        id: u.id,
        name: getUserDisplayName(u),
        role: getUserRole(u),
        avatar: u.avatar,
        posts: u.posts || 0,
        reactions: u.reactions || 0,
        comments: u.comments || 0,
        reshares: u.reshares || 0,
        impressions: u.impressions || 0
      }))
  })

  // Editors (users with role 'editor' - these are content managers who manage profiles)
  const editors = computed(() => {
    return users.value
      .filter(u => u.role === 'editor' || u.userType === 'Editor')
      .map(u => ({
        id: u.id,
        name: getUserDisplayName(u),
        departments: u.departments || [],
        avatar: u.avatar,
        assignedUsers: u.assignedUsers || 0
      }))
  })

  // Contributors/Profiles - ALL users in the organization should be shown here
  // In B2B context, these are the LinkedIn profiles being managed
  const contributors = computed(() => {
    console.log('[UsersStore.contributors] Computing contributors from', users.value.length, 'users')
    // Show all users as contributors/profiles - they all can have content created for them
    const result = users.value.map(u => ({
      id: u.id,
      name: getUserDisplayName(u),
      role: getUserRole(u),
      avatar: u.avatar,
      assignedEvents: u.assignedEvents || 0,
      pendingPosts: u.pendingPosts || 0
    }))
    console.log('[UsersStore.contributors] Returning', result.length, 'contributors')
    return result
  })

  // Simple users list (for UserSettingsView)
  const simpleUsers = computed(() => {
    return users.value.map(u => ({
      id: u.id,
      name: getUserDisplayName(u),
      role: getUserRole(u),
      avatar: u.avatar
    }))
  })

  // Content items with creator info
  const contentItemsWithCreators = computed(() => {
    return contentItems.value.map(item => {
      const user = getUserById(item.userId)
      return {
        ...item,
        creator: user ? {
          name: getUserDisplayName(user),
          role: getUserRole(user),
          avatar: user.avatar
        } : undefined
      }
    })
  })

  // Transform backend API response to local User format
  function transformTeamMemberToUser(member: APIUserResponse): User {
    const nameParts = (member.name || '').split(' ')
    const firstName = member.first_name || nameParts[0] || ''
    const lastName = member.last_name || nameParts.slice(1).join(' ') || ''

    // Map role to userType - handle both lowercase and capitalized versions
    const normalizedRole = (member.role || '').toLowerCase()
    let userType: string = 'Profile'
    if (normalizedRole === 'admin') {
      userType = 'Admin'
    } else if (normalizedRole === 'editor') {
      userType = 'Editor'
    }

    // Find sub_admin name if sub_admin_id exists
    let subAdminName = ''
    if (member.sub_admin_id) {
      // We'll need to look this up later, for now store the ID
      subAdminName = 'N/A' // Will be populated when we have access to all users
    }

    const user: User = {
      id: member.id,
      firstName,
      lastName,
      name: member.name || `${firstName} ${lastName}`.trim() || member.email?.split('@')[0] || 'Unknown',
      jobTitle: member.job_title || member.role || '',
      department: member.department_name || '',
      departmentId: member.department_id || undefined,  // Store department_id
      userType,
      subAdmin: subAdminName,
      subAdminId: member.sub_admin_id || undefined,  // Store sub_admin_id
      linkedInProfile: '',
      status: member.linkedin_connected ? 'Connected' : 'Not Connected',
      email: member.email,
      creatorType: member.posts_count && member.posts_count > 0 ? 'Creator' : 'Non-Creator',
      avatar: member.avatar_url || undefined,
      posts: member.posts_count || 0,
      reactions: 0,
      comments: 0,
      reshares: 0,
      impressions: 0,
      departments: [],
      assignedUsers: 0,
      assignedEvents: 0,
      pendingPosts: 0,
      role: normalizedRole || undefined
    }

    console.log('[UsersStore.transform] Transformed user:', member.email, '-> role:', normalizedRole, 'userType:', userType)
    return user
  }

  // Transform backend ActivityLogEntry to local Activity format
  function transformActivityLogEntry(entry: ActivityLogEntry): Activity {
    const user = getUserById(entry.user_id)
    return {
      id: entry.id,
      userId: entry.user_id,
      userName: user ? getUserDisplayName(user) : 'Unknown User',
      activityType: entry.action_type,
      activityName: entry.description,
      timestamp: new Date(entry.created_at).toLocaleString()
    }
  }

  // Transform backend invitation to local PendingInvitation format
  function transformInvitation(inv: APIPendingInvitation): PendingInvitation {
    const emailParts = inv.email.split('@')[0].split('.')
    return {
      id: inv.id,
      firstName: emailParts[0] || '',
      lastName: emailParts[1] || '',
      email: inv.email,
      status: inv.status === 'pending' ? 'Pending Registration' : inv.status,
      statusIcon: 'information-circle'
    }
  }

  // Actions
  function removeActiveContributor(userId: string) {
    const index = users.value.findIndex(u => u.id === userId)
    if (index !== -1) {
      users.value.splice(index, 1)
    }
  }

  function removePendingInvitation(invitationId: string) {
    const index = pendingInvitations.value.findIndex(i => i.id === invitationId)
    if (index !== -1) {
      pendingInvitations.value.splice(index, 1)
    }
  }

  function addUser(user: User) {
    users.value.push(user)
  }

  function updateUser(userId: string, updates: Partial<User>) {
    const user = users.value.find(u => u.id === userId)
    if (user) {
      Object.assign(user, updates)
    }
  }

  function addActivity(activity: Activity) {
    activities.value.push(activity)
  }

  function addContentItem(item: ContentItem) {
    contentItems.value.push(item)
  }

  function updateContentItem(itemId: string, updates: Partial<ContentItem>) {
    const item = contentItems.value.find(i => i.id === itemId)
    if (item) {
      Object.assign(item, updates)
    }
  }

  // Backend API Actions

  /**
   * Enrich editor users with assigned profiles count
   * This is called after fetchUsers to populate the assignedUsers field
   */
  async function enrichEditorsWithAssignedProfilesCount() {
    try {
      // Import profileService dynamically to avoid circular dependencies
      const profileService = (await import('../services/profileService')).default

      // Find all editor users
      const editorUsers = users.value.filter(u => u.role === 'editor' || u.userType === 'Editor')

      console.log('[UsersStore.enrichEditorsWithAssignedProfilesCount] Found', editorUsers.length, 'editors to enrich')

      // Fetch assigned profiles for each editor in parallel
      const profileCountPromises = editorUsers.map(async (editor) => {
        try {
          const profiles = await profileService.getUserAssignedProfiles(editor.id)
          console.log('[UsersStore.enrichEditorsWithAssignedProfilesCount] Editor', editor.name, 'has', profiles.length, 'assigned profiles')
          return { editorId: editor.id, count: profiles.length }
        } catch (error) {
          console.error('[UsersStore.enrichEditorsWithAssignedProfilesCount] Failed to fetch profiles for editor', editor.id, error)
          return { editorId: editor.id, count: 0 }
        }
      })

      const profileCounts = await Promise.all(profileCountPromises)

      // Update assignedUsers for each editor
      profileCounts.forEach(({ editorId, count }) => {
        const editor = users.value.find(u => u.id === editorId)
        if (editor) {
          editor.assignedUsers = count
        }
      })

      console.log('[UsersStore.enrichEditorsWithAssignedProfilesCount] Enrichment complete')
    } catch (error) {
      console.error('[UsersStore.enrichEditorsWithAssignedProfilesCount] Failed:', error)
      // Don't throw - this is a non-critical enrichment
    }
  }

  /**
   * Refresh assigned profiles count for a specific editor
   * Called after assigning/unassigning profiles
   */
  async function refreshEditorAssignedCount(editorId: string) {
    try {
      const profileService = (await import('../services/profileService')).default
      const profiles = await profileService.getUserAssignedProfiles(editorId)

      const editor = users.value.find(u => u.id === editorId)
      if (editor) {
        editor.assignedUsers = profiles.length
        console.log('[UsersStore.refreshEditorAssignedCount] Updated editor', editor.name, 'assigned count to', profiles.length)
      }
    } catch (error) {
      console.error('[UsersStore.refreshEditorAssignedCount] Failed:', error)
    }
  }

  /**
   * Fetch all users from backend
   */
  async function fetchUsers() {
    try {
      isLoading.value = true
      error.value = null

      console.log('[UsersStore.fetchUsers] Starting fetch...')
      console.log('[UsersStore.fetchUsers] Access token present:', !!localStorage.getItem('access_token'))

      const fetchedUsers = await userService.getUsers()
      console.log('[UsersStore.fetchUsers] Raw response:', fetchedUsers)
      console.log('[UsersStore.fetchUsers] Response type:', typeof fetchedUsers, Array.isArray(fetchedUsers))

      if (!fetchedUsers || !Array.isArray(fetchedUsers)) {
        console.warn('[UsersStore.fetchUsers] Invalid response format, expected array')
        users.value = []
        return { success: true, data: [] }
      }

      // Transform backend users to local User format
      users.value = fetchedUsers.map(transformTeamMemberToUser)

      // Post-process: Populate subAdmin names from sub_admin_id
      users.value.forEach(user => {
        if (user.subAdminId && (!user.subAdmin || user.subAdmin === 'N/A')) {
          const editor = users.value.find(u => u.id === user.subAdminId && (u.role === 'editor' || u.userType === 'Editor'))
          if (editor) {
            user.subAdmin = getUserDisplayName(editor)
          }
        }
      })

      console.log('[UsersStore.fetchUsers] Transformed users:', users.value)
      console.log('[UsersStore.fetchUsers] Fetched', users.value.length, 'users')
      console.log('[UsersStore.fetchUsers] Contributors computed:', contributors.value.length)
      console.log('[UsersStore.fetchUsers] Editors computed:', editors.value.length)

      // Post-process: Fetch assigned profiles count for editors
      await enrichEditorsWithAssignedProfilesCount()

      return { success: true, data: fetchedUsers }
    } catch (err: any) {
      console.error('[UsersStore.fetchUsers] Failed:', err)
      console.error('[UsersStore.fetchUsers] Error details:', {
        message: err.message,
        status: err.status,
        data: err.data,
        response: err.response
      })

      // If it's a 404 "not part of organization" error, log it clearly
      if (err.message?.includes('organization')) {
        console.error('[UsersStore.fetchUsers] User is not part of an organization! Check user\'s organization_id in database.')
      }

      error.value = err.message || 'Failed to fetch users'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch pending invitations from backend
   */
  async function fetchPendingInvitations() {
    try {
      isLoading.value = true
      error.value = null

      const fetchedInvitations = await userService.getPendingInvitations()

      // Transform to local format
      pendingInvitations.value = fetchedInvitations.map(transformInvitation)

      console.log('[UsersStore.fetchPendingInvitations] Fetched', pendingInvitations.value.length, 'invitations')
      return { success: true, data: fetchedInvitations }
    } catch (err: any) {
      console.error('[UsersStore.fetchPendingInvitations] Failed:', err)
      error.value = err.message || 'Failed to fetch invitations'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch activity log from backend
   */
  async function fetchActivityLog(limit: number = 50) {
    try {
      isLoading.value = true
      error.value = null

      const fetchedActivity = await activityService.getActivityLog({ limit })

      // Transform to local format
      activities.value = fetchedActivity.map(transformActivityLogEntry)

      console.log('[UsersStore.fetchActivityLog] Fetched', activities.value.length, 'activities')
      return { success: true, data: fetchedActivity }
    } catch (err: any) {
      console.error('[UsersStore.fetchActivityLog] Failed:', err)
      error.value = err.message || 'Failed to fetch activity log'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Invite a new user
   */
  async function inviteUser(userData: InviteUserData) {
    try {
      isLoading.value = true
      error.value = null

      const newInvitation = await userService.inviteUser(userData)

      // Add to pending invitations
      pendingInvitations.value.push(transformInvitation(newInvitation))

      console.log('[UsersStore.inviteUser] Invited:', userData.email)
      return { success: true, data: newInvitation }
    } catch (err: any) {
      console.error('[UsersStore.inviteUser] Failed:', err)
      error.value = err.message || 'Failed to invite user'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cancel a pending invitation
   */
  async function cancelInvitation(invitationId: string) {
    try {
      isLoading.value = true
      error.value = null

      await userService.cancelInvitation(invitationId)

      // Remove from local state
      removePendingInvitation(invitationId)

      console.log('[UsersStore.cancelInvitation] Cancelled:', invitationId)
      return { success: true }
    } catch (err: any) {
      console.error('[UsersStore.cancelInvitation] Failed:', err)
      error.value = err.message || 'Failed to cancel invitation'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Remove a user from the team
   */
  async function removeUser(userId: string) {
    try {
      isLoading.value = true
      error.value = null

      await userService.removeUser(userId)

      // Remove from local state
      const index = users.value.findIndex(u => u.id === userId)
      if (index !== -1) {
        users.value.splice(index, 1)
      }

      console.log('[UsersStore.removeUser] Removed:', userId)
      return { success: true }
    } catch (err: any) {
      console.error('[UsersStore.removeUser] Failed:', err)
      error.value = err.message || 'Failed to remove user'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update user details
   */
  async function updateUserApi(userId: string, updates: Partial<UserWithRole>) {
    try {
      isLoading.value = true
      error.value = null

      const updatedUser = await userService.updateUser(userId, updates)

      // Update in local state
      const user = users.value.find(u => u.id === userId)
      if (user) {
        const transformed = transformTeamMemberToUser(updatedUser as unknown as APIUserResponse)
        Object.assign(user, transformed)
      }

      console.log('[UsersStore.updateUserApi] Updated:', userId)
      return { success: true, data: updatedUser }
    } catch (err: any) {
      console.error('[UsersStore.updateUserApi] Failed:', err)
      error.value = err.message || 'Failed to update user'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Resend a pending invitation email
   */
  async function resendInvitation(invitationId: string) {
    try {
      const result = await userService.resendInvitation(invitationId)
      console.log('[UsersStore.resendInvitation] Resent:', invitationId)
      return { success: true, data: result }
    } catch (err: any) {
      console.error('[UsersStore.resendInvitation] Failed:', err)
      return { success: false, error: err.message, status: (err as any).status }
    }
  }

  /**
   * Send a custom reminder email to a team member
   */
  async function sendTeamMemberReminder(userId: string, data: { subject: string; message: string }) {
    try {
      const result = await userService.sendTeamMemberReminder(userId, data)
      console.log('[UsersStore.sendTeamMemberReminder] Sent to:', userId)
      return { success: true, data: result }
    } catch (err: any) {
      console.error('[UsersStore.sendTeamMemberReminder] Failed:', err)
      return { success: false, error: err.message }
    }
  }

  /**
   * Initialize store with data from backend
   */
  async function initializeStore() {
    console.log('[UsersStore.initializeStore] Initializing store...')
    await Promise.all([
      fetchUsers(),
      fetchPendingInvitations(),
      fetchActivityLog()
    ])
    console.log('[UsersStore.initializeStore] Store initialized')
  }

  /**
   * Clear error state
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    users,
    organization,
    activeContributors,
    pendingInvitations,
    activities,
    contentItems,
    isLoading,
    error,

    // Computed
    totalActiveContributors,
    totalPendingInvitations,
    totalUsers,
    analyticsUsers,
    editors,
    contributors,
    simpleUsers,
    contentItemsWithCreators,

    // Local Actions
    removeActiveContributor,
    removePendingInvitation,
    getUserById,
    getUserDisplayName,
    getUserRole,
    addUser,
    updateUser,
    addActivity,
    addContentItem,
    updateContentItem,

    // API Actions
    initializeStore,
    fetchUsers,
    fetchPendingInvitations,
    fetchActivityLog,
    inviteUser,
    cancelInvitation,
    removeUser,
    updateUserApi,
    resendInvitation,
    sendTeamMemberReminder,
    clearError,
    refreshEditorAssignedCount
  }
})
